import React, { ReactNode } from 'react';

interface IconWrapperProps {
  /** The SVG content to be rendered inside the wrapper */
  children: ReactNode;
  /** Optional title for accessibility and tooltips */
  title?: string;
  /** Size in pixels for both width and height. Defaults to 16px */
  size?: number;
}

interface StatusIconProps {
  /** Size in pixels for both width and height. Defaults to 16px */
  size?: number;
  /** Optional className for additional styling */
  className?: string;
}

const IconWrapper: React.FC<IconWrapperProps> = ({ children, title, size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label={title}
    className="fill-[url(#lightGradient)] dark:fill-[url(#darkGradient)]"
  >
    <defs>
      <linearGradient
        id="darkGradient"
        x1="12.1302"
        y1="-0.700991"
        x2="7.5223"
        y2="11.0728"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#25262E" />
        <stop offset="1" stopColor="#25262E" stopOpacity="0.67" />
      </linearGradient>

      <linearGradient
        id="lightGradient"
        x1="7.75332"
        y1="-0.509204"
        x2="5.02183"
        y2="6.8293"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="white" />
        <stop offset="1" stopColor="white" stopOpacity="0.67" />
      </linearGradient>
      <linearGradient
        id="successGradient"
        x1="19.0013"
        y1="15.3327"
        x2="-1.42727"
        y2="-0.905413"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#3EBE6A" />
        <stop offset="1" stopColor="#42DE64" stopOpacity="0.7" />
      </linearGradient>
      <linearGradient
        id="warningGradient"
        x1="19.0013"
        y1="15.3327"
        x2="-1.42727"
        y2="-0.905413"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#F97F12" />
        <stop offset="1" stopColor="#FFBC65" />
      </linearGradient>
      <linearGradient
        id="errorGradient"
        x1="19.0013"
        y1="15.3327"
        x2="-1.42727"
        y2="-0.905413"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#F55E66" />
        <stop offset="1" stopColor="#FF848A" />
      </linearGradient>
      <linearGradient
        id="unknownGradient"
        x1="19.0028"
        y1="15.3332"
        x2="-1.4258"
        y2="-0.904924"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#B7B7B7" />
        <stop offset="1" stopColor="#C8C8C8" />
      </linearGradient>
    </defs>
    {title && <title>{title}</title>}
    {children}
  </svg>
);

export const PositiveIcon: React.FC<StatusIconProps> = ({ size, className }) => (
  <IconWrapper title="Success" size={size}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.0013 0.666016C3.95121 0.666016 0.667969 3.94926 0.667969 7.99935C0.667969 12.0494 3.95121 15.3327 8.0013 15.3327C12.0514 15.3327 15.3346 12.0494 15.3346 7.99935C15.3346 3.94926 12.0514 0.666016 8.0013 0.666016Z"
      fill="url(#successGradient)"
      className={className}
    />
    <path d="M11.474 6.4701C11.7344 6.20975 11.7344 5.78764 11.474 5.52729C11.2137 5.26694 10.7915 5.26694 10.5312 5.52729L7.0026 9.05589L5.47401 7.52729C5.21366 7.26694 4.79155 7.26694 4.5312 7.52729C4.27085 7.78764 4.27085 8.20975 4.5312 8.4701L6.5312 10.4701C6.79155 10.7305 7.21366 10.7305 7.47401 10.4701L11.474 6.4701Z" />
  </IconWrapper>
);

export const AttentionIcon: React.FC<StatusIconProps> = ({ size, className }) => (
  <IconWrapper title="Warning" size={size}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.0013 0.666016C3.95121 0.666016 0.667969 3.94926 0.667969 7.99935C0.667969 12.0494 3.95121 15.3327 8.0013 15.3327C12.0514 15.3327 15.3346 12.0494 15.3346 7.99935C15.3346 3.94926 12.0514 0.666016 8.0013 0.666016Z"
      fill="url(#warningGradient)"
      className={className}
    />
    <path d="M10.474 5.52729C10.7344 5.78764 10.7344 6.20975 10.474 6.4701L8.94541 7.9987L10.474 9.52729C10.7344 9.78764 10.7344 10.2098 10.474 10.4701C10.2137 10.7305 9.79155 10.7305 9.5312 10.4701L8.0026 8.94151L6.47401 10.4701C6.21366 10.7305 5.79155 10.7305 5.5312 10.4701C5.27085 10.2098 5.27085 9.78764 5.5312 9.52729L7.0598 7.9987L5.5312 6.4701C5.27085 6.20975 5.27085 5.78764 5.5312 5.52729C5.79155 5.26694 6.21366 5.26694 6.47401 5.52729L8.0026 7.05589L9.5312 5.52729C9.79155 5.26694 10.2137 5.26694 10.474 5.52729Z" />
  </IconWrapper>
);

export const RiskLevelIcon: React.FC<StatusIconProps> = ({ size, className }) => (
  <IconWrapper title="Error" size={size}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.0013 0.666016C3.95121 0.666016 0.667969 3.94926 0.667969 7.99935C0.667969 12.0494 3.95121 15.3327 8.0013 15.3327C12.0514 15.3327 15.3346 12.0494 15.3346 7.99935C15.3346 3.94926 12.0514 0.666016 8.0013 0.666016Z"
      fill="url(#errorGradient)"
      className={className}
    />
    <path d="M10.474 5.52729C10.7344 5.78764 10.7344 6.20975 10.474 6.4701L8.94541 7.9987L10.474 9.52729C10.7344 9.78764 10.7344 10.2098 10.474 10.4701C10.2137 10.7305 9.79155 10.7305 9.5312 10.4701L8.0026 8.94151L6.47401 10.4701C6.21366 10.7305 5.79155 10.7305 5.5312 10.4701C5.27085 10.2098 5.27085 9.78764 5.5312 9.52729L7.0598 7.9987L5.5312 6.4701C5.27085 6.20975 5.27085 5.78764 5.5312 5.52729C5.79155 5.26694 6.21366 5.26694 6.47401 5.52729L8.0026 7.05589L9.5312 5.52729C9.79155 5.26694 10.2137 5.26694 10.474 5.52729Z" />
  </IconWrapper>
);

export const UnknownIcon: React.FC<StatusIconProps> = ({ size, className }) => (
  <IconWrapper title="Unknown" size={size}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.00277 0.666504C3.95268 0.666504 0.669434 3.94975 0.669434 7.99984C0.669434 12.0499 3.95268 15.3332 8.00277 15.3332C12.0529 15.3332 15.3361 12.0499 15.3361 7.99984C15.3361 3.94975 12.0529 0.666504 8.00277 0.666504Z"
      fill="url(#unknownGradient)"
      className={className}
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.65913 6.44807C7.82857 6.34849 8.02778 6.31209 8.22149 6.34532C8.41519 6.37854 8.59089 6.47925 8.71746 6.62961C8.84403 6.77996 8.9133 6.97026 8.91301 7.16679V7.16779C8.91301 7.3135 8.79489 7.51428 8.46043 7.73725C8.3135 7.83521 8.16181 7.91152 8.04423 7.96378L7.87429 8.03267C7.52019 8.15188 7.3321 8.52878 7.44834 8.87749C7.56477 9.22678 7.94232 9.41556 8.29161 9.29912L8.38253 9.26624C8.74922 9.10954 8.97225 8.9985 9.20003 8.84665C9.61497 8.57002 10.2462 8.02145 10.2463 7.16808C10.2469 6.65691 10.0667 6.162 9.73749 5.77094C9.40814 5.37969 8.95095 5.11763 8.4469 5.03118C7.94284 4.94472 7.42446 5.03944 6.98355 5.29856C6.54264 5.55769 6.20767 5.96449 6.03796 6.44693C5.91578 6.79426 6.09829 7.17487 6.44562 7.29705C6.79295 7.41923 7.17356 7.23671 7.29574 6.88939C7.36096 6.70399 7.48969 6.54765 7.65913 6.44807zM8.1001 10C7.73191 10 7.43343 10.2985 7.43343 10.6667C7.43343 11.0349 7.73191 11.3333 8.1001 11.3333H8.10676C8.47495 11.3333 8.77343 11.0349 8.77343 10.6667C8.77343 10.2985 8.47495 10 8.10676 10H8.1001z"
    />
  </IconWrapper>
);
